import React, { useState } from 'react';

interface PartsLookupFormProps {
  orgName: string;
  onSubmit: (phoneNumber: string) => Promise<void>;
  submitting: boolean;
  heading?: string;
  intro?: string;
  consentDescription?: string;
  submitLabel?: string;
  submittingLabel?: string;
}

function PartsLookupForm({
  orgName,
  onSubmit,
  submitting,
  heading = 'Parts Lookup Sign-Up',
  intro = "Enter your phone number to sign up for SMS parts availability checks. Text any part number to see if it's in stock.",
  consentDescription = 'I agree to receive SMS messages for parts lookup',
  submitLabel = 'Sign Up for Parts Lookup',
  submittingLabel = 'Signing Up...',
}: PartsLookupFormProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!phoneNumber.trim()) {
      setError('Please enter your mobile phone number.');
      return;
    }

    if (!smsConsent) {
      setError('Please check the SMS consent box to continue.');
      return;
    }

    await onSubmit(phoneNumber);
  };

  const isValid = phoneNumber.trim().length > 0 && smsConsent;

  return (
    <form className="parts-lookup-form" onSubmit={handleSubmit}>
      <div className="form-step">
        <h2>{heading}</h2>
        <p className="step-intro">{intro}</p>

        <div className="form-group">
          <label htmlFor="mobilePhone" className="required">Mobile Phone Number</label>
          <input
            type="tel"
            id="mobilePhone"
            name="mobilePhone"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e.target.value);
              if (error) setError(null);
            }}
            placeholder="(555) 123-4567"
            disabled={submitting}
          />
        </div>

        <div className="sms-consent-section">
          <div className="form-group">
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="smsConsent"
                name="smsConsent"
                checked={smsConsent}
                onChange={(e) => {
                  setSmsConsent(e.target.checked);
                  if (error) setError(null);
                }}
                disabled={submitting}
              />
              <label htmlFor="smsConsent">
                {consentDescription}
                <span className="sms-disclosure">
                  I consent to receive automated SMS messages from <strong>{orgName}</strong> (powered by Opervia) regarding parts availability, stock checks, and pickup notifications. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase. <a href="https://opervia.com/sms-terms" target="_blank" rel="noopener noreferrer">SMS Terms</a> | <a href="https://opervia.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </span>
              </label>
            </div>
          </div>
        </div>

        {error && <div className="error-text">{error}</div>}

        <button
          type="submit"
          className="btn btn-primary parts-lookup-submit"
          disabled={!isValid || submitting}
        >
          {submitting ? submittingLabel : submitLabel}
        </button>
      </div>
    </form>
  );
}

export default PartsLookupForm;
